﻿import React, { useState, useEffect } from 'react';
import { UserDataStore } from '../store/userDataStore';
import { BoilingPointBenchSheet } from '../models/boiling-point-bench-sheet';
import { BenchSheetTechnologyType, NoteType, DocumentType, Entities } from '../models/enums';
import { DocumentationConstants } from '../store/documentation-constants';
import SecondaryBenchSheetInstrument from './SecondaryBenchSheetInstrument';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import BpMpMrTemperaturesData from './BpMpMrTemperaturesData';
import BenchSheetData from './BenchSheetData';
import NoteViewer from './NoteViewer';
import { FieldUpdate } from '../models/field-update';
import { CrudFunctions } from '../utilities/crud-functions';
import BenchSheetToolbar from './BenchSheetToolbar';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import TicketLogEntryForm from './TicketLogEntryForm';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import DmaFooter from './DmaFooter';

import NumberBox from 'devextreme-react/number-box';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

export default function BoilingPointBenchSheetForm() {
    const [canEdit, setCanEdit] =
        useState<boolean>(false);

    const [benchSheet, setBenchSheet] =
        useState<BoilingPointBenchSheet>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        setAuthorizations();
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    key++;

    return (
        <div className="pw-form-container">
            <div className="pw-content">
                <BenchSheetToolbar title="BP Bench Sheet"
                    subtitle="Boiling Point"
                    auditRowPointer={benchSheetId} entity={Entities.BpBenchSheets}
                    technologyId={BenchSheetTechnologyType.BoilingPoint}
                    documentationId={DocumentationConstants.BPBenchSheets}
                    showItemInformation={showBenchSheetInformationDialog}
                    showTicketLog={showTicketLogEntryDialog} />

                <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

                <SecondaryBenchSheetInstrument benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    technology={BenchSheetTechnologyType.BoilingPoint}
                    title="Boiling Point" />

                <div className='pw-comp-row pw-margin-top-05'>
                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Station Pressure (inHg)</div>
                        <NumberBox id="stationPressure" readOnly={!canEdit}
                            placeholder='NA' className="textbox"
                            onValueChanged={onValueChanged}
                            value={benchSheet && benchSheet.stationPressure ? benchSheet.stationPressure : undefined} />
                    </div>
                </div>

                <BpMpMrTemperaturesData benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    datagridHeight='20rem'
                    technologyId={BenchSheetTechnologyType.BoilingPoint}
                    title="Boiling Point Temperatures" />

                <div className="pw-comp-row-scroll-fixed-grid">
                    <NoteViewer height='10rem' title="Bench Sheet Note"
                        noteType={NoteType.Method} parentId={benchSheetId} />
                </div>

                <div className="pw-comp-row-scroll-fixed-grid">
                    <BenchSheetData parentId={benchSheetId}
                        readOnly={!canEdit} canDelete={true}
                        technology={BenchSheetTechnologyType.BoilingPoint}
                        documentType={DocumentType.General}
                        hideImageHandler={true} />
                </div>
            </div>

            <DmaFooter />

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="Boiling Point Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function setAuthorizations() {
        const user = UserDataStore.getCurrentUser();

        const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
        const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

        setCanEdit(canEdit);
    }

    async function onValueChanged(e: any) {
        const caller = e.element.id as string;

        if (caller && benchSheet) {
            // Is all this needed? Its not being done this way anywhere else
            const bs = benchSheet as any;
            const currentValue = bs[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                setBenchSheet({ ...benchSheet, [caller]: e.value });

                let fieldUpdate: FieldUpdate = {
                    entity: "BpMpMrBenchSheets",
                    field: caller,
                    value: e.value,
                    rowId: benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Field");
                    });
            }
        }
    }

    //GET methods

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    // PUT functions

    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/benchsheetfield", fieldUpdate);
    }

    // other functions

    async function getAllData() {
        await getBenchSheet();
    }

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}


